/**
 * Family Members (since 2000) — the dashboard detail page for the
 * /report-management/family-members-by-year-of-birth report
 * (rpt_NewFamSince2000).
 *
 * Laid out as the Birthdays list is, read against the year of birth instead of
 * the month: the year heads its own block in black, at the size of the rows
 * under it, with the number born in it beside it in grey — and under it one row
 * per member, the name and then the head of their line and its colour. No
 * column titles: the two of them read for themselves under the year.
 *
 *     2001 (2)
 *       Amara      Mary • Blue Line
 *
 * The list is one grid spanning every year, so the two columns line up down the
 * whole page and each is exactly as wide as its longest entry — a `max-content`
 * track with a fixed column-gap, the way Generations & Ages lays its table out.
 * Nothing wraps; a list wider than the card scrolls sideways instead. See
 * `.family-members-year-list` in App.css.
 *
 * The date of birth is not printed, but it is what orders the rows inside a
 * year, so a year still reads in calendar order.
 *
 * The rows, the years and the count are the report endpoint's own (see
 * dashboardFamilyMembersByYear.js): descendants and their living spouses born
 * from 2000 on, the deceased and the divorced already left out, each row
 * carrying its date of birth, line colour and head of line. The card's count
 * comes through the same helper, so the two cannot drift apart.
 * /dashboard-statistics-with-details is read for what the report rows do not
 * name: the name markers — the in-law asterisk, the deceased angel and the
 * multiple-birth tag — and the member photos, which come off each row's roster
 * record, the way Total Family Members prints them. A row the roster does not
 * know falls back to the endpoint's own `is_spouse` for the asterisk.
 *
 * A Year of Birth picker leads the filter bar — All Years, then every year the
 * report holds — narrowing the page to one year. The rest of the bar is the
 * shared report one, carrying the sorts alone; the two age sorts also turn the
 * year blocks around, since on a list grouped by year of birth the oldest
 * people are the earliest years. No member tabs: the endpoint lists living
 * descendants and their living spouses and nothing else, so the deceased tabs
 * could only ever come back empty. See docs/dashboard-filter-rules.md.
 */

import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { getApi } from "../../../Services/apiService";
import { getFamilyTreeIdFromStorage } from "../../../Utils/headOfLineRole";
import DashboardBackButton from "./DashboardBackButton";
import DashboardColorLegend from "./DashboardColorLegend";
import DashboardDetailSearchBox, {
  DashboardDetailSearchNoMatch,
} from "./DashboardDetailSearchBox";
import DashboardDetailSortFilterBar from "./DashboardDetailSortFilterBar";
import DashboardLineColorName from "./DashboardLineColorName";
import DashboardLineTotalsPopup, {
  buildDashboardLineTotals,
  familyLineKey,
} from "./DashboardLineTotalsPopup";
import DashboardMarkerLegend from "./DashboardMarkerLegend";
import DashboardPeriodDropdown from "./DashboardPeriodDropdown";
import {
  DashboardDeceasedAngel,
  DashboardInLawAsterisk,
  DashboardMemberNameLabel,
  DashboardNameMarkerGroup,
  DashboardPhotoModal,
  DashboardPhotoName,
  getDashboardMemberNamePhotoUrls,
  useDashboardPhotoModal,
} from "./DashboardPhotoViewer";
import {
  FAMILY_MEMBERS_SINCE_YEAR,
  fetchFamilyMembersByYearOfBirth,
  splitFamilyMembersByYearBlocks,
  toFamilyMembersByYearBlocks,
} from "../../../Utils/dashboardFamilyMembersByYear";
import { filterDashboardMembersBySearch } from "../../../Utils/dashboardDetailSearch";
import { getMemberDobTimestamp } from "../../../Utils/family-tree/dateAndTime";
import {
  isDashboardDeceasedMember,
  isDashboardInLawMember,
} from "../../../Utils/dashboardMemberDisplayName";
import { isExplicitDashboardStartOrTopOfLineMember } from "../../../Utils/dashboardStartOfLineMember";
import {
  getMultipleBirthLegendTags,
  getMultipleBirthSuperscriptText,
} from "../../../Utils/dashboardMultipleBirthSuperscript";
import {
  buildDashboardColorLegendLines,
  buildHeadsOfLineIdByColorMap,
  extractAllMembersFromStatisticsResponse,
  extractHeadsOfLineFromStatisticsResponse,
} from "../../../Utils/dashboardFamilyLineNavigation";

/**
 * Year leads the bar as a picker rather than a sort: the list is grouped by
 * year already, so what the button is good for is narrowing to one of them.
 * Which way the years run is the job of the two age sorts — see sortedYears.
 */
const ALL_YEARS = "all";

/** The order the report opens in: the years as the endpoint sends them. */
const DEFAULT_SORT = "none";

/** Printed where the payload names no year of birth. */
const MISSING_VALUE = "—";

function FamilyMembersSinceYear() {
  const { photos: photoModalPhotos, openPhoto, closePhoto } =
    useDashboardPhotoModal();
  const [sortBy, setSortBy] = useState(DEFAULT_SORT);
  // Picking the Line Color sort opens the per-line totals popup.
  const [showLineTotals, setShowLineTotals] = useState(false);
  const handleSortChange = (next) => {
    setSortBy(next);
    setShowLineTotals(next === "color");
  };
  const closeLineTotals = useCallback(() => setShowLineTotals(false), []);
  const [selectedYear, setSelectedYear] = useState(ALL_YEARS);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [report, setReport] = useState({
    startYear: FAMILY_MEMBERS_SINCE_YEAR,
    totalCount: 0,
    years: [],
  });
  // The family lines the footer key and the head-of-line names are built from.
  // The report endpoint names a row's colour but not who heads that line, so
  // the lines come off the same statistics payload the other detail pages read.
  const [legendLines, setLegendLines] = useState([]);
  // The same roster, by member id: the report endpoint sends neither the
  // deceased status, the in-law flag nor the multiple-birth fields, so the
  // markers beside a name are read off that member's roster record.
  const [rosterById, setRosterById] = useState(() => new Map());
  const [isCompactLayout, setIsCompactLayout] = useState(
    () => typeof window !== "undefined" && window.innerWidth <= 768
  );

  useEffect(() => {
    const updateLayoutMode = () => setIsCompactLayout(window.innerWidth <= 768);

    updateLayoutMode();
    window.addEventListener("resize", updateLayoutMode);

    return () => window.removeEventListener("resize", updateLayoutMode);
  }, []);

  useEffect(() => {
    const fetchReport = async () => {
      try {
        setLoading(true);
        setReport(
          await fetchFamilyMembersByYearOfBirth(getFamilyTreeIdFromStorage())
        );
      } catch (error) {
        console.error("Error fetching family members by year of birth:", error);
        setReport({
          startYear: FAMILY_MEMBERS_SINCE_YEAR,
          totalCount: 0,
          years: [],
        });
      } finally {
        setLoading(false);
      }
    };

    fetchReport();
  }, []);

  // Settled on its own: a call that failed leaves the report itself on screen
  // with only the head-of-line names, the photos and the name markers missing.
  useEffect(() => {
    const fetchRoster = async () => {
      try {
        const family_tree_id = getFamilyTreeIdFromStorage();
        if (!family_tree_id) return;

        const response = await getApi(
          `/dashboard-statistics-with-details?family_tree_id=${family_tree_id}`
        );
        if (response?.statusCode !== 200 || !response?.data) return;

        const members = extractAllMembersFromStatisticsResponse(response.data);
        setRosterById(
          new Map(
            members
              .map((member) => [
                String(member?.id ?? member?.member_id ?? ""),
                member,
              ])
              .filter(([id]) => id)
          )
        );
        setLegendLines(
          buildDashboardColorLegendLines(
            members,
            buildHeadsOfLineIdByColorMap(
              extractHeadsOfLineFromStatisticsResponse(response.data),
              members
            )
          )
        );
      } catch (error) {
        console.error("Error fetching family roster:", error);
      }
    };

    fetchRoster();
  }, []);

  /**
   * The record a row's markers and its photo are read from: its roster record
   * where the roster knows it, else the report's own row — which carries the
   * spouse flag but none of the other fields, so it reads as a living member
   * and prints the asterisk off `is_spouse`.
   */
  const getMarkerMember = useCallback(
    (row) =>
      rosterById.get(row.id) ?? {
        ...row.member,
        is_in_laws: row.isSpouse,
        present_or_deceased: row.member?.present_or_deceased ?? 1,
      },
    [rosterById]
  );

  const isSearching = searchQuery.trim().length > 0;
  const isYearPicked = selectedYear !== ALL_YEARS;

  /** Every year in the report, plus the All Years the picker opens on. */
  const yearOptions = useMemo(
    () => [
      { value: ALL_YEARS, label: "All Years" },
      ...report.years
        .filter((group) => group.year !== null)
        .map((group) => ({ value: group.year, label: String(group.year) })),
    ],
    [report.years]
  );

  // A year that the report no longer holds — the tree changed under a picked
  // year — falls back to All Years rather than showing an empty page.
  useEffect(() => {
    if (!isYearPicked) return;
    if (report.years.some((group) => String(group.year) === String(selectedYear))) {
      return;
    }
    setSelectedYear(ALL_YEARS);
  }, [report.years, selectedYear, isYearPicked]);

  /**
   * The years the picker and the search box leave on screen, each holding the
   * rows that matched. A year whose every row was filtered out drops with them.
   */
  const visibleYears = useMemo(() => {
    const picked = isYearPicked
      ? report.years.filter(
          (group) => String(group.year) === String(selectedYear)
        )
      : report.years;

    if (!isSearching) return picked;

    return picked
      .map((group) => ({
        ...group,
        rows: filterDashboardMembersBySearch(
          group.rows,
          searchQuery,
          (row) => row.sortName,
          getMarkerMember
        ),
      }))
      .filter((group) => group.rows.length > 0);
  }, [
    report.years,
    isYearPicked,
    selectedYear,
    isSearching,
    searchQuery,
    getMarkerMember,
  ]);

  const visibleRowCount = useMemo(
    () => visibleYears.reduce((sum, group) => sum + group.rows.length, 0),
    [visibleYears]
  );

  /**
   * The order the list reads in. Every sort keeps the years as blocks and
   * re-orders the members inside each one, so the report stays "by year of
   * birth" under all of them. The two age sorts also turn the blocks around,
   * since on a list grouped by year of birth the oldest people are the earliest
   * years: Oldest to Youngest runs 2000 forward, Youngest to Oldest runs back.
   */
  const sortedYears = useMemo(() => {
    const compareDob = (a, b, { oldestFirst }) => {
      const left = getMemberDobTimestamp(getMarkerMember(a));
      const right = getMemberDobTimestamp(getMarkerMember(b));
      if (left === right) return 0;
      if (left === -1) return 1;
      if (right === -1) return -1;
      return oldestFirst ? left - right : right - left;
    };

    // The order inside a year with nothing else asked of it: calendar order,
    // which is how the helper already sorted them.
    const byBirthDate = (a, b) =>
      (a.birthMonth ?? 13) - (b.birthMonth ?? 13) ||
      (a.birthDay ?? 32) - (b.birthDay ?? 32) ||
      a.sortName.localeCompare(b.sortName);

    const compareRows = (() => {
      switch (sortBy) {
        case "alpha-asc":
          return (a, b) => a.sortName.localeCompare(b.sortName) || byBirthDate(a, b);
        case "alpha-desc":
          return (a, b) => b.sortName.localeCompare(a.sortName) || byBirthDate(a, b);
        // Grouped by line, and each line's members A–Z by name within it.
        case "color":
          return (a, b) =>
            a.colorName.localeCompare(b.colorName) ||
            a.sortName.localeCompare(b.sortName) ||
            byBirthDate(a, b);
        case "oldest":
          return (a, b) => compareDob(a, b, { oldestFirst: true }) || byBirthDate(a, b);
        case "youngest":
          return (a, b) => compareDob(a, b, { oldestFirst: false }) || byBirthDate(a, b);
        default:
          return byBirthDate;
      }
    })();

    const direction = sortBy === "youngest" ? -1 : 1;

    return visibleYears
      .map((group) => ({ ...group, rows: [...group.rows].sort(compareRows) }))
      .sort((a, b) => {
        if (a.year === null) return 1;
        if (b.year === null) return -1;
        return direction * (a.year - b.year);
      });
  }, [visibleYears, sortBy, getMarkerMember]);

  const blocks = useMemo(
    () => toFamilyMembersByYearBlocks(sortedYears),
    [sortedYears]
  );

  // The two-column split, as Family Members does it: the blocks are laid out
  // once more in a hidden measuring list, and the first column takes as many
  // whole blocks as fit the columns' visible height; the rest scroll in the
  // second column. A phone never splits — one scrolling column instead.
  const columnsRef = useRef(null);
  const measureListRef = useRef(null);
  const firstColumnRef = useRef(null);
  const [splitAt, setSplitAt] = useState(0);
  // The split the measurement last asked for. The trim effect below only acts
  // once the rendered split has caught up with it — the commit that swaps the
  // blocks still shows the old split, and trimming that stale column would eat
  // into the fresh measurement one row per render.
  const measuredSplitRef = useRef(0);

  useLayoutEffect(() => {
    if (loading || blocks.length === 0 || isCompactLayout) {
      measuredSplitRef.current = blocks.length;
      setSplitAt(blocks.length);
      return undefined;
    }

    const columnsEl = columnsRef.current;
    const measureEl = measureListRef.current;
    if (!columnsEl || !measureEl) return undefined;

    const calculateSplit = () => {
      const availableHeight = columnsEl.clientHeight;
      const wrappers = Array.from(measureEl.children);
      if (availableHeight <= 0 || wrappers.length === 0) {
        measuredSplitRef.current = blocks.length;
        setSplitAt(blocks.length);
        return;
      }

      let cumulative = 0;
      let nextSplit = wrappers.length;
      for (let index = 0; index < wrappers.length; index += 1) {
        cumulative += wrappers[index].offsetHeight;
        if (cumulative > availableHeight) {
          nextSplit = Math.max(1, index);
          break;
        }
      }

      measuredSplitRef.current = nextSplit;
      setSplitAt(nextSplit);
    };

    calculateSplit();

    // Measured again whenever the visible height changes or the blocks
    // themselves do — a web font landing, or the roster's markers arriving
    // after the report, both re-flow the rows.
    const observer = new ResizeObserver(calculateSplit);
    observer.observe(columnsEl);
    observer.observe(measureEl);
    window.addEventListener("resize", calculateSplit);
    let cancelled = false;
    document.fonts?.ready?.then(() => {
      if (!cancelled) calculateSplit();
    });

    return () => {
      cancelled = true;
      observer.disconnect();
      window.removeEventListener("resize", calculateSplit);
    };
  }, [blocks, loading, isCompactLayout]);

  // A phone is always one column, whatever split a wider window left behind.
  const columns = useMemo(
    () =>
      splitFamilyMembersByYearBlocks(
        blocks,
        isCompactLayout ? blocks.length : splitAt
      ),
    [blocks, splitAt, isCompactLayout]
  );
  const isSingleColumn = columns.length === 1;

  // After render, trim the first column if its last row still runs past the
  // visible area — the shared grid can sit a row taller than the measure.
  useLayoutEffect(() => {
    if (loading || isCompactLayout || isSingleColumn) return;
    if (splitAt !== measuredSplitRef.current) return;

    const columnsEl = columnsRef.current;
    const firstColEl = firstColumnRef.current;
    if (!columnsEl || !firstColEl) return;

    const lastCell = firstColEl.lastElementChild;
    if (!lastCell) return;

    const columnsBottom = columnsEl.getBoundingClientRect().bottom;
    if (lastCell.getBoundingClientRect().bottom > columnsBottom + 0.5) {
      const trimmed = Math.max(1, splitAt - 1);
      measuredSplitRef.current = trimmed;
      setSplitAt(trimmed);
    }
  }, [columns, loading, isCompactLayout, isSingleColumn, splitAt]);

  /**
   * Head of line per colour, from the lines the footer key is built from — so
   * the name printed beside a colour on a row is spelled exactly as that key
   * spells it. Keyed on colour + hex, with a colour-only fallback for rows whose
   * hex is spelled differently from the heads-of-line record's.
   */
  const headNameByLine = useMemo(() => {
    const byColorAndHex = new Map();
    const byColor = new Map();

    legendLines.forEach(({ headName, colorName, hexCode }) => {
      if (!headName) return;
      byColorAndHex.set(familyLineKey(colorName, hexCode), headName);
      const colorOnly = familyLineKey(colorName, "");
      if (!byColor.has(colorOnly)) byColor.set(colorOnly, headName);
    });

    return { byColorAndHex, byColor };
  }, [legendLines]);

  const getLineHeadName = (colorName, hexCode) =>
    headNameByLine.byColorAndHex.get(familyLineKey(colorName, hexCode)) ||
    headNameByLine.byColor.get(familyLineKey(colorName, "")) ||
    "";

  /**
   * How many of the rows on screen each family line holds, for the Line Color
   * popup. Counted off the same rows the total in the heading counts, so the
   * lines add up to it under a year pick or a search too.
   */
  const lineTotals = useMemo(
    () =>
      buildDashboardLineTotals(
        visibleYears.flatMap((group) => group.rows),
        legendLines
      ),
    [visibleYears, legendLines]
  );

  /** Leads the bar, where the Year sort toggle used to sit. */
  const yearPicker = (
    <DashboardPeriodDropdown
      label="Year of Birth"
      options={yearOptions}
      value={selectedYear}
      onChange={setSelectedYear}
      className="family-members-year-picker"
    />
  );

  const renderRow = (row) => {
    // The markers after the name, by the rules Total Family Members prints
    // them: the asterisk for an in-law who does not start a line, the angel for
    // the deceased, the Twin/Trip/Quad tag for a multiple birth.
    const markerMember = getMarkerMember(row);
    const showInLawAsterisk =
      isDashboardInLawMember(markerMember) &&
      !isExplicitDashboardStartOrTopOfLineMember(markerMember);
    const isDeceased = isDashboardDeceasedMember(markerMember);
    const multipleBirthSup = getMultipleBirthSuperscriptText(markerMember);

    return (
      <React.Fragment key={row.id}>
        <div className="family-members-year-cell family-members-year-name">
          <DashboardNameMarkerGroup>
            <DashboardPhotoName
              {...getDashboardMemberNamePhotoUrls(markerMember)}
              onViewPhoto={openPhoto}
            >
              <DashboardMemberNameLabel
                baseName={row.baseName}
                descendantSpouseName={row.descendantSpouseName}
                afterBaseName={
                  showInLawAsterisk && row.descendantSpouseName ? (
                    <DashboardInLawAsterisk />
                  ) : null
                }
              />
            </DashboardPhotoName>
            {showInLawAsterisk && !row.descendantSpouseName && (
              <DashboardInLawAsterisk />
            )}
            {isDeceased && <DashboardDeceasedAngel />}
            {multipleBirthSup && (
              <sup className="dashboard-multiple-birth-sup">{multipleBirthSup}</sup>
            )}
          </DashboardNameMarkerGroup>
        </div>
        {/* The wide layout has room to name who heads the line right here, so
            it does — and carries no family-line key in its footer. The phone
            layout prints the colour alone and leaves the heads to that key. */}
        <div className="family-members-year-cell family-members-year-line">
          <DashboardLineColorName
            colorName={row.colorName}
            hexCode={row.hexCode}
            headName={
              isCompactLayout ? "" : getLineHeadName(row.colorName, row.hexCode)
            }
          />
        </div>
      </React.Fragment>
    );
  };

  /**
   * A year's heading. Spans the grid, so the rows below share one set of
   * columns rather than each aligning to itself. The count is how many rows are
   * under it right now, so a search that thins a year is reflected in its
   * heading.
   */
  const renderHeading = (group, continued) => (
    <h4 className="family-members-year-heading dashboard-section-heading">
      {group.year === null ? MISSING_VALUE : group.year}
      <span className="family-members-year-heading-count dashboard-section-heading-count">
        {` (${group.rows.length})`}
      </span>
      {continued && (
        <span className="family-members-year-heading-continued">(cont.)</span>
      )}
    </h4>
  );

  const renderBlock = (block) => (
    <React.Fragment key={block.key}>
      {block.kind === "heading" && renderHeading(block.group, block.continued)}
      {block.kind === "row" && renderRow(block.row)}
    </React.Fragment>
  );

  return (
    <div className="dashboard-container family-members-since-year-page dashboard-detail-page">
      <div className="dash-box dashboard-detail-box">
        <div className="dashboard-detail-header">
          <div className="dashboard-detail-header-left">
            <DashboardBackButton />
            {/* Title and count are one cluster, as on the sibling reports, so
                the total never wraps under the heading on a narrow phone —
                this title is long enough that two loose flex items broke
                across two lines. */}
            <div className="dashboard-detail-heading-label-cluster">
              <h3 className="dashboard-detail-title">
                Family Members (Since {report.startYear})
              </h3>
              {/* With All Years showing and nothing searched this is the
                  endpoint's own total — the same number the dashboard card
                  shows — and otherwise however many rows the picker or the
                  search leaves. */}
              <div className="dashboard-detail-count">
                {isSearching || isYearPicked
                  ? visibleRowCount
                  : report.totalCount}
              </div>
            </div>
            {!loading && report.years.length > 0 && (
              <DashboardDetailSearchBox
                value={searchQuery}
                onChange={setSearchQuery}
              />
            )}
            {!loading && report.years.length > 0 && (
              /* Sorts alone: the endpoint lists living descendants and their
                 living spouses and nothing else, so the deceased tabs could
                 only ever come back empty. */
              <DashboardDetailSortFilterBar
                showMemberFilters={false}
                sortBy={sortBy}
                onSortChange={handleSortChange}
                leadingSortButtons={yearPicker}
              />
            )}
          </div>
        </div>

        {loading ? (
          <div className="dashboard-detail-empty-state">
            <p>Loading family members...</p>
          </div>
        ) : sortedYears.length === 0 ? (
          <div className="dashboard-detail-empty-state">
            {isSearching ? (
              <DashboardDetailSearchNoMatch query={searchQuery} />
            ) : (
              <p>No data for this period.</p>
            )}
          </div>
        ) : (
          <div className="family-members-since-year-page dashboard-detail-content-wrapper">
            {/* Hidden copy of every block, laid out once each so the split
                can be measured against the visible height. */}
            {!isCompactLayout && (
              <div
                ref={measureListRef}
                className="family-members-year-list-measure"
                aria-hidden="true"
              >
                {blocks.map((block) => (
                  <div
                    key={block.key}
                    className="family-members-year-list dashboard-detail-list"
                  >
                    {renderBlock(block)}
                  </div>
                ))}
              </div>
            )}

            {/* No column titles: the two columns — name, line — read for
                themselves under the year heading. */}
            <div ref={columnsRef} className="family-members-year-columns">
              <div
                ref={firstColumnRef}
                className={`family-members-year-list dashboard-detail-list family-members-year-list-first${
                  isSingleColumn ? " family-members-year-list--scrollable" : ""
                }`}
              >
                {columns[0].map(renderBlock)}
              </div>
              {!isSingleColumn && (
                <>
                  <div className="family-members-year-column-divider" />
                  <div className="family-members-year-list dashboard-detail-list family-members-year-list-scroll">
                    {columns[1].map(renderBlock)}
                  </div>
                </>
              )}
            </div>

            <div className="family-members-since-year-page in-law-footer">
              {/* The multiple-birth tags the key lists are the ones on the rows
                  above it, as on Total Family Members. */}
              <DashboardMarkerLegend
                multipleBirthTags={getMultipleBirthLegendTags(
                  sortedYears.flatMap((group) => group.rows.map(getMarkerMember))
                )}
              />
              {/* Family lines are a phone-only key, as on the sibling reports:
                  the wide layout has room to name a line on the row itself. */}
              {isCompactLayout && <DashboardColorLegend lines={legendLines} />}
            </div>
          </div>
        )}
      </div>

      <DashboardLineTotalsPopup
        open={showLineTotals}
        onClose={closeLineTotals}
        totals={lineTotals}
      />
      <DashboardPhotoModal photos={photoModalPhotos} onClose={closePhoto} />
    </div>
  );
}

export default FamilyMembersSinceYear;
